export type CronLogStatus = 'success' | 'partial' | 'error'

export interface ProcessDailyResult {
  birthdays_processed: number
  birthdays_benefits_granted: number
  recurring_processed: number
  recurring_benefits_granted: number
  expired_benefits: number
  expired_vouchers: number
  expiring_notifications_sent: number
  category_changes: number
  category_upgrades: number
  category_downgrades: number
  benefits_cancelled_downgrade: number
  benefits_cancelled_upgrade: number
  errors: string[]
}

export interface CronLog {
  id: number
  started_at: string
  finished_at: string
  duration_seconds: number
  status: CronLogStatus
  result: ProcessDailyResult | null
  error_message: string | null
  trigger_source: string
}

export interface CronLogFilters {
  page: number
  limit: number
  status?: CronLogStatus
}

export interface CronLogPaginatedResponse {
  data: CronLog[]
  total_items: number
  current_page: number
  total_pages: number
  current_items_count: number
}

export const CRON_STATUS_LABELS: Record<CronLogStatus, string> = {
  success: 'Exitoso',
  partial: 'Parcial',
  error: 'Error',
}

export const CRON_STATUS_COLORS: Record<CronLogStatus, { bg: string; text: string }> = {
  success: { bg: 'bg-green-100', text: 'text-green-800' },
  partial: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  error: { bg: 'bg-red-100', text: 'text-red-800' },
}

export const TRIGGER_SOURCE_LABELS: Record<string, string> = {
  cron: 'CRON automático',
  admin_manual: 'Admin manual',
}

// ============================================================================
// Cron Event Logs (granular)
// ============================================================================

export type CronEventType =
  | 'benefit_granted'
  | 'benefit_expired'
  | 'voucher_expired'
  | 'notification_sent'
  | 'category_upgrade'
  | 'category_downgrade'
  | 'benefit_cancelled'
  | 'error'

export type CronJobName = 'birthday' | 'recurring' | 'expire' | 'notify' | 'category'

export interface CronEventLog {
  id: number
  id_execution: number
  id_company: number
  id_client: number | null
  event_type: CronEventType
  entity_type: string | null
  entity_id: number | null
  job_name: CronJobName
  detail: Record<string, unknown> | null
  created_at: string
}

export interface CronEventFilters {
  page: number
  limit: number
  event_type?: CronEventType
  job_name?: CronJobName
}

export interface CronEventPaginatedResponse {
  data: CronEventLog[]
  total_items: number
  current_page: number
  total_pages: number
  current_items_count: number
}

export const EVENT_TYPE_LABELS: Record<CronEventType, string> = {
  benefit_granted: 'Beneficio otorgado',
  benefit_expired: 'Beneficio expirado',
  voucher_expired: 'Voucher expirado',
  notification_sent: 'Notificación enviada',
  category_upgrade: 'Upgrade categoría',
  category_downgrade: 'Downgrade categoría',
  benefit_cancelled: 'Beneficio cancelado',
  error: 'Error',
}

export const EVENT_TYPE_COLORS: Record<CronEventType, { bg: string; text: string }> = {
  benefit_granted: { bg: 'bg-green-100', text: 'text-green-800' },
  benefit_expired: { bg: 'bg-gray-100', text: 'text-gray-800' },
  voucher_expired: { bg: 'bg-gray-100', text: 'text-gray-800' },
  notification_sent: { bg: 'bg-blue-100', text: 'text-blue-800' },
  category_upgrade: { bg: 'bg-purple-100', text: 'text-purple-800' },
  category_downgrade: { bg: 'bg-orange-100', text: 'text-orange-800' },
  benefit_cancelled: { bg: 'bg-red-100', text: 'text-red-800' },
  error: { bg: 'bg-red-100', text: 'text-red-800' },
}

export const JOB_NAME_LABELS: Record<CronJobName, string> = {
  birthday: 'Cumpleaños',
  recurring: 'Recurrencias',
  expire: 'Expiración',
  notify: 'Notificaciones',
  category: 'Categorías',
}
